import { useCallback, useEffect, useRef, useState } from 'react';
import type { CalendarEventDTO, GoogleProfile } from '../../global';
import { notifyGoogleProfileChanged } from '../account/useGoogleAccount';
import { t } from '../../lib/i18n';

export type CalendarStatusKind =
  | 'idle'
  | 'not_connected'
  | 'connecting'
  | 'loading'
  | 'ready'
  | 'auth_expired'
  | 'error';

export interface CalendarState {
  status: CalendarStatusKind;
  events: CalendarEventDTO[];
  profile?: GoogleProfile;
  /** Bağlanma denemesi başarısız olduğunda dolu — arayüzde ayrıca gösterilir. */
  connectError: string | null;
  message?: string;
}

const REFRESH_MS = 5 * 60 * 1000;

const INITIAL: CalendarState = {
  status: 'idle',
  events: [],
  connectError: null,
};

/**
 * Google Takvim durumunun tek sahibi.
 *
 * Önceki hâlindeki üç sorun burada çözülüyor:
 * 1. Arka plan yenilemesi her seferinde durumu 'loading'e çekip listeyi
 *    boşaltıyordu — kart 5 dakikada bir gözle görülür şekilde titriyordu.
 *    Artık ilk yükleme dışında sessiz yenileme yapılıyor.
 * 2. Zamanlayıcı yalnızca "bağlıysa" etkinlik çekiyordu; kullanıcı Google
 *    tarafından yetkiyi iptal ettiğinde arayüz eski listeyi süresiz
 *    gösteriyordu. Artık bağlantı kopması da duruma yansıyor.
 * 3. Bileşen ayrıldıktan sonra gelen yanıtlar state'e yazıyordu.
 */
export function useCalendar() {
  const [state, setState] = useState<CalendarState>(INITIAL);
  const mountedRef = useRef(true);
  // Eşzamanlı iki yükleme yarışırsa yalnızca sonuncusu state'e yazsın.
  const requestIdRef = useRef(0);

  const apply = useCallback((updater: (prev: CalendarState) => CalendarState, id: number) => {
    if (!mountedRef.current || id !== requestIdRef.current) return;
    setState(updater);
  }, []);

  /**
   * @param silent Arka plan yenilemesi — mevcut liste ekranda kalsın, sadece
   *               veri değişirse güncellensin.
   */
  const loadEvents = useCallback(
    async (silent = false) => {
      const id = ++requestIdRef.current;
      if (!silent) {
        apply((prev) => ({ ...prev, status: 'loading' }), id);
      }

      try {
        const result = await window.zabit.getUpcomingCalendarEvents();

        if (Array.isArray(result)) {
          apply((prev) => ({ ...prev, status: 'ready', events: result, connectError: null }), id);
          return;
        }

        if (result.error === 'not_connected') {
          apply(() => ({ ...INITIAL, status: 'not_connected' }), id);
        } else if (result.error === 'auth_expired') {
          apply((prev) => ({ ...prev, status: 'auth_expired', events: [] }), id);
        } else {
          apply(
            (prev) => ({
              ...prev,
              status: 'error',
              message: t(
                'Takvim yüklenemedi. İnternet bağlantınızı kontrol edin.',
                'Could not load the calendar. Check your internet connection.'
              ),
            }),
            id
          );
        }
      } catch {
        apply(
          (prev) => ({ ...prev, status: 'error', message: t('Takvim yüklenemedi.', 'Could not load the calendar.') }),
          id
        );
      }
    },
    [apply]
  );

  const syncStatus = useCallback(
    async (silent: boolean) => {
      try {
        const { connected, profile } = await window.zabit.getGoogleCalendarStatus();

        if (!connected) {
          if (mountedRef.current) {
            // Bağlantı gerçekten koptuysa listeyi de temizle — eski
            // etkinlikleri "hâlâ bağlıymış gibi" göstermeye devam etme.
            requestIdRef.current += 1;
            setState({ ...INITIAL, status: 'not_connected' });
          }
          return;
        }

        if (mountedRef.current) {
          setState((prev) => ({ ...prev, profile }));
        }
        await loadEvents(silent);
      } catch {
        // Durum sorgusu başarısızsa mevcut görünümü bozma.
      }
    },
    [loadEvents]
  );

  useEffect(() => {
    mountedRef.current = true;
    void syncStatus(false);

    const interval = window.setInterval(() => void syncStatus(true), REFRESH_MS);

    return () => {
      mountedRef.current = false;
      window.clearInterval(interval);
    };
  }, [syncStatus]);

  const connect = useCallback(async () => {
    setState((prev) => ({ ...prev, status: 'connecting', connectError: null }));
    try {
      const result = await window.zabit.connectGoogleCalendar();
      if (!mountedRef.current) return;

      if (result.ok) {
        setState((prev) => ({ ...prev, profile: result.profile, connectError: null }));
        // Kenar çubuğundaki hesap kartı da yenilensin.
        notifyGoogleProfileChanged();
        await loadEvents(false);
      } else {
        setState({ ...INITIAL, status: 'not_connected', connectError: result.error });
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setState({
        ...INITIAL,
        status: 'not_connected',
        connectError: (err as Error).message,
      });
    }
  }, [loadEvents]);

  const disconnect = useCallback(async () => {
    try {
      await window.zabit.disconnectGoogleCalendar();
    } finally {
      notifyGoogleProfileChanged();
      if (mountedRef.current) {
        requestIdRef.current += 1;
        setState({ ...INITIAL, status: 'not_connected' });
      }
    }
  }, []);

  const refresh = useCallback(() => loadEvents(false), [loadEvents]);

  return { ...state, connect, disconnect, refresh };
}
